import {
  Avatar,
  Button,
  Chip,
  Input,
  Link as HeroLink,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  FiChevronDown,
  FiChevronRight,
  FiMapPin,
  FiSearch,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

import type {
  AssignableLocation,
  DirectoryMember,
  MemberAssignment,
  MemberCount,
} from '../../types';
import {
  locationLabel,
  memberInitials,
  memberName,
  tierStyle,
} from '../../utils';

const ROWS_PER_PAGE_CHOICES = ['10', '25', '50'];

/** Long enough that typing a name is one read, not one per keystroke. */
const SEARCH_SETTLE_MS = 300;

const assignmentKey = (assignment: MemberAssignment): string =>
  `${assignment.locationId}-${assignment.role.id}`;

/**
 * What a row can be made to do. Absent for a read that only reports, so the
 * table renders the same rows without the column that changes them.
 */
export interface MemberTableActions {
  isWorking: boolean;
  /** Their only assignment is being dropped, which also ends their membership. */
  onRemoveOnlyAssignment: (member: DirectoryMember) => void;
  /** They hold several, so the whole set is edited at once. */
  onEditAssignments: (member: DirectoryMember) => void;
  /** One grant dropped from the expanded list. */
  onRemoveAssignment: (
    member: DirectoryMember,
    assignment: MemberAssignment
  ) => void;
}

export interface MemberTableProps {
  members: DirectoryMember[];
  locations: AssignableLocation[];
  /** All three narrow the read itself, not the page that came back. */
  searchTerm: string;
  onSearchTermChange: (searchTerm: string) => void;
  /** Omitted where the controls are not offered — see showPlaceAndRoleFilters. */
  locationFilter?: string[];
  onLocationFilterChange?: (locationIds: string[]) => void;
  roleFilter?: string[];
  onRoleFilterChange?: (teamRoleIds: string[]) => void;
  isLoading: boolean;
  /**
   * How many members match the filters in total. A floor once the workspace is
   * larger than the service counts, and null until the first read returns.
   */
  totalCount: MemberCount | null;
  /** The read is cursored and forward-only, so there is no page count. */
  pageSize: string;
  onPageSizeChange: (rows: string) => void;
  pageNumber: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onFirstPage: () => void;
  /** Omitted for a read-only table. */
  actions?: MemberTableActions;
  /**
   * Whether each row carries the member's roles and where they hold them. The
   * directory is read by anyone in the workspace, so it is listed by name only
   * and a member's roles are opened one at a time instead.
   */
  showAssignments?: boolean;
  /**
   * Whether the place and role controls are offered. The directory answers by
   * name alone, so it has nothing to narrow by place or role.
   */
  showPlaceAndRoleFilters?: boolean;
  /** Opens one member. Absent where a row is not selectable. */
  onSelectMember?: (member: DirectoryMember) => void;
  /** Opens where that member can work. Absent where the row does not offer it. */
  onShowWorkplaces?: (member: DirectoryMember) => void;
}

/**
 * Past the service's bound the count is a floor, so it renders as "500+
 * members" — the reader is told the list is larger than the number, never given
 * a number that looks exact and is not.
 */
const memberCountLabel = (totalCount: MemberCount): string => {
  const suffix = totalCount.count === 1 ? 'member' : 'members';
  return totalCount.kind === 'AtLeast'
    ? `${totalCount.count}+ ${suffix}`
    : `${totalCount.count} ${suffix}`;
};

/** One grant: the place, read under the tier above it, and the role held there. */
const AssignmentChip: React.FC<{ assignment: MemberAssignment }> = ({
  assignment,
}) => {
  const tier = tierStyle(assignment.hierarchyType);

  return (
    <Chip size="sm" variant="flat" className="h-auto py-1">
      <span className="flex items-center gap-1.5">
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${tier.dot}`}
          aria-hidden
        />
        <span className="text-default-500">
          {assignment.parentName && `${assignment.parentName} · `}
          {locationLabel(assignment.locationName, assignment.locationId)}
        </span>
        <span className="text-default-300" aria-hidden>
          |
        </span>
        <span className="font-medium text-default-700">
          {assignment.role.displayName}
        </span>
      </span>
    </Chip>
  );
};

export const MemberTable: React.FC<MemberTableProps> = ({
  members,
  locations,
  searchTerm,
  onSearchTermChange,
  locationFilter = [],
  onLocationFilterChange = () => {},
  roleFilter = [],
  onRoleFilterChange = () => {},
  isLoading,
  totalCount,
  pageSize,
  onPageSizeChange,
  pageNumber,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onFirstPage,
  actions,
  showAssignments = true,
  showPlaceAndRoleFilters = true,
  onSelectMember,
  onShowWorkplaces,
}) => {
  // The box keeps its own value so typing stays responsive; the read follows
  // once the typing settles.
  const [typed, setTyped] = useState(searchTerm);
  const [expanded, setExpanded] = useState<string[]>([]);

  // Every role in the workspace, named once, so the filter offers the whole
  // set rather than only the roles that happen to be held today.
  const roleChoices = useMemo(() => {
    const byId = new Map<string, string>();
    locations.forEach((location) =>
      location.roles.forEach((role) => byId.set(role.id, role.displayName))
    );
    return [...byId].map(([id, displayName]) => ({ id, displayName }));
  }, [locations]);

  useEffect(() => {
    if (typed === searchTerm) {
      return undefined;
    }

    const settle = setTimeout(
      () => onSearchTermChange(typed),
      SEARCH_SETTLE_MS
    );
    return () => clearTimeout(settle);
  }, [typed, searchTerm, onSearchTermChange]);

  // Every control is part of the query, and so is the page: `members` is one
  // page of the directory, not a slice of everything fetched.
  const rows = members;

  const toggle = (userId: string) =>
    setExpanded((open) =>
      open.includes(userId)
        ? open.filter((id) => id !== userId)
        : [...open, userId]
    );

  // Narrowing asks a different question, and the hook answers it from page one.
  const narrow = (apply: () => void) => apply();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="max-w-xs"
          size="sm"
          variant="bordered"
          placeholder="Search"
          value={typed}
          onValueChange={(value) => narrow(() => setTyped(value))}
          startContent={<FiSearch className="text-default-400" />}
          isClearable
          onClear={() => narrow(() => setTyped(''))}
        />
        {showPlaceAndRoleFilters && (
          <>
            <Select
              className="max-w-[200px]"
              size="sm"
              variant="bordered"
              selectionMode="multiple"
              placeholder="All locations"
              aria-label="Filter by location"
              isDisabled={isLoading}
              selectedKeys={locationFilter}
              onSelectionChange={(keys) =>
                narrow(() => onLocationFilterChange([...keys].map(String)))
              }
            >
              {locations.map((location) => (
                <SelectItem
                  key={location.locationId}
                  textValue={locationLabel(
                    location.locationName,
                    location.locationId
                  )}
                >
                  {locationLabel(location.locationName, location.locationId)}
                </SelectItem>
              ))}
            </Select>
            <Select
              className="max-w-[180px]"
              size="sm"
              variant="bordered"
              selectionMode="multiple"
              placeholder="All roles"
              aria-label="Filter by role"
              isDisabled={isLoading}
              selectedKeys={roleFilter}
              onSelectionChange={(keys) =>
                narrow(() => onRoleFilterChange([...keys].map(String)))
              }
            >
              {roleChoices.map((role) => (
                <SelectItem key={role.id}>{role.displayName}</SelectItem>
              ))}
            </Select>
          </>
        )}
      </div>

      <Table
        aria-label="Team members"
        removeWrapper
        // The rows on screen are the previous filter's until the new read
        // lands; dimming says so without emptying the table.
        className={isLoading ? 'opacity-60 transition-opacity' : undefined}
        classNames={{
          // A solid hairline under every row, and column rules that stop short
          // of it so the grid reads without boxing each cell in.
          th: 'bg-transparent text-default-500 border-b border-default-100 relative after:absolute after:right-0 after:top-2 after:bottom-2 after:w-px after:bg-default-100 last:after:hidden',
          td: 'py-3 border-b border-default-100 relative after:absolute after:right-0 after:top-2 after:bottom-2 after:w-px after:bg-default-100 last:after:hidden',
        }}
      >
        <TableHeader>
          {[
            <TableColumn key="member" width="26%">
              Team member
            </TableColumn>,
            <TableColumn key="phone" width="16%">
              Contact number
            </TableColumn>,
            <TableColumn key="email" width="20%">
              Email address
            </TableColumn>,
            ...(showAssignments
              ? [
                  <TableColumn
                    key="assignments"
                    width={actions ? '24%' : '38%'}
                  >
                    Assignments
                  </TableColumn>,
                ]
              : []),
            ...(actions
              ? [
                  <TableColumn key="actions" width="14%" align="end">
                    {' '}
                  </TableColumn>,
                ]
              : []),
          ]}
        </TableHeader>
        <TableBody
          emptyContent={
            searchTerm || locationFilter.length > 0 || roleFilter.length > 0
              ? 'Nobody matches that search.'
              : 'No team members yet.'
          }
        >
          {rows.flatMap((member) => {
            const [first, ...rest] = member.assignments;
            const isOpen = expanded.includes(member.userId);

            const summary = (
              <TableRow key={member.userId}>
                {[
                  <TableCell key="member">
                    <div className="flex items-center gap-2">
                      {showAssignments && rest.length > 0 ? (
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          aria-label={
                            isOpen ? 'Hide assignments' : 'Show all assignments'
                          }
                          onPress={() => toggle(member.userId)}
                        >
                          {isOpen ? <FiChevronDown /> : <FiChevronRight />}
                        </Button>
                      ) : (
                        <span className="w-8" aria-hidden />
                      )}
                      <Avatar
                        size="sm"
                        src={member.avatarUrl ?? undefined}
                        name={memberInitials(member.name)}
                        showFallback
                      />
                      {onSelectMember ? (
                        // onPress, not onClick: the row's own press handling
                        // swallows a plain DOM click inside a cell.
                        <HeroLink
                          as="button"
                          size="sm"
                          className="font-medium text-foreground hover:text-primary hover:underline"
                          onPress={() => onSelectMember(member)}
                        >
                          {memberName(member.name)}
                        </HeroLink>
                      ) : (
                        <Link
                          to={`/users/${member.userId}`}
                          className="font-medium text-foreground hover:text-primary hover:underline"
                        >
                          {memberName(member.name)}
                        </Link>
                      )}
                      {onShowWorkplaces && (
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          aria-label="Show where they can work"
                          onPress={() => onShowWorkplaces(member)}
                        >
                          <FiMapPin className="text-default-400" />
                        </Button>
                      )}
                    </div>
                  </TableCell>,
                  <TableCell key="phone" className="text-default-600">
                    {member.phone ?? '-'}
                  </TableCell>,
                  <TableCell key="email" className="text-default-600">
                    {member.email ?? '-'}
                  </TableCell>,
                  ...(showAssignments
                    ? [
                        <TableCell key="assignments">
                          {isOpen ? (
                            <span className="text-default-400">
                              {member.assignments.length} assignments listed
                              below
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              {first ? (
                                <AssignmentChip assignment={first} />
                              ) : (
                                '-'
                              )}
                              {rest.length > 0 && (
                                <Chip size="sm" variant="flat">
                                  +{rest.length}
                                </Chip>
                              )}
                            </div>
                          )}
                        </TableCell>,
                      ]
                    : []),
                  ...(actions
                    ? [
                        <TableCell key="actions">
                          {rest.length > 0 ? (
                            <Button
                              size="sm"
                              variant="flat"
                              isDisabled={actions.isWorking}
                              onPress={() => actions.onEditAssignments(member)}
                            >
                              Edit assignments
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="light"
                              color="danger"
                              isDisabled={actions.isWorking || !first}
                              onPress={() =>
                                actions.onRemoveOnlyAssignment(member)
                              }
                            >
                              Remove role
                            </Button>
                          )}
                        </TableCell>,
                      ]
                    : []),
                ]}
              </TableRow>
            );

            if (!showAssignments || !isOpen) {
              return [summary];
            }

            // Every pair, not just the extras: the first assignment is the one
            // the summary row collapses, and leaving it out here is what made
            // a location look like it held no role.
            const detail = member.assignments.map((assignment) => (
              <TableRow
                key={`${member.userId}-${assignmentKey(assignment)}`}
                className="bg-default-50"
              >
                {[
                  <TableCell key="member"> </TableCell>,
                  <TableCell key="phone"> </TableCell>,
                  <TableCell key="email"> </TableCell>,
                  <TableCell key="assignments">
                    <AssignmentChip assignment={assignment} />
                  </TableCell>,
                  ...(actions
                    ? [
                        <TableCell key="actions">
                          <Button
                            size="sm"
                            variant="light"
                            color="danger"
                            isDisabled={actions.isWorking}
                            onPress={() =>
                              actions.onRemoveAssignment(member, assignment)
                            }
                          >
                            Remove role
                          </Button>
                        </TableCell>,
                      ]
                    : []),
                ]}
              </TableRow>
            ));

            return [summary, ...detail];
          })}
        </TableBody>
      </Table>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-small text-default-500">
          <span>Rows per page:</span>
          <Select
            className="w-20"
            size="sm"
            variant="bordered"
            aria-label="Rows per page"
            selectedKeys={[pageSize]}
            onSelectionChange={(keys) =>
              onPageSizeChange(String([...keys][0] ?? '10'))
            }
          >
            {ROWS_PER_PAGE_CHOICES.map((choice) => (
              <SelectItem key={choice}>{choice}</SelectItem>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-3 text-small text-default-500">
          {totalCount && <span>{memberCountLabel(totalCount)}</span>}
          {/* The read hands back a cursor, so there is a next page or there is
              not. The total does not number the pages: past the service's bound
              it is a floor, so it cannot say how many pages remain. */}
          <span>Page {pageNumber}</span>
          <Button
            size="sm"
            variant="flat"
            isDisabled={!hasPreviousPage || isLoading}
            onPress={onFirstPage}
          >
            First
          </Button>
          <Button
            size="sm"
            variant="flat"
            isDisabled={!hasPreviousPage || isLoading}
            onPress={onPreviousPage}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="flat"
            isDisabled={!hasNextPage || isLoading}
            onPress={onNextPage}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
